import moment from "moment-timezone";
import API from "serviceshift-ui/api-client";

import { formatDuration } from "@/mixins";

const TASK_STATUSES_WITH_EXTRA_DATA = {
  Enqueued: {
    color: "var(--background-task-enqueued)",
    icon: "fa-layer-group",
    statusName: "Queued"
  },
  Processing: {
    color: "var(--background-task-processing)",
    icon: "fa-hourglass-start",
    statusName: "In Progress"
  },
  Ready: {
    color: "var( --background-task-ready)",
    icon: "fa-check",
    statusName: "Completed"
  },
  Failed: {
    color: "var(--background-task-failed)",
    icon: "fa-frown",
    statusName: "Failed"
  },
  ReadyWarning: {
    color: "var( --background-task-ready)",
    icon: "fa-triangle-exclamation",
    statusName: "Completed"
  }
};

const getTime = (date, status) =>
  date ? moment(date).format(`[${status} at] hh:mm a`) : null;
const getDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : null);
const getTotalTime = (start, end) => {
  const duration = moment.duration(moment(end).diff(moment(start)));

  return formatDuration(duration.minutes(), true);
};

export function taskAdapter(task) {
  let status = task.status;
  // Shows complete with warning icon for pricebook import with row errors
  if (task?.message?.includes("view the rows with errors")) {
    status = "ReadyWarning";
  }
  return {
    ...task,
    ...TASK_STATUSES_WITH_EXTRA_DATA[status],
    enqueuedDate: getDate(task.enqueuedAt),
    startDate: getDate(task.startedAt),
    finishDate: getDate(task.finishedAt),
    enqueuedTime: getTime(task.enqueuedAt, "Queued"),
    startedTime: getTime(task.startedAt, "Started"),
    completedTime: getTime(task.finishedAt, "Completed"),
    failedTime: getTime(task.finishedAt, "Failed"),
    linkExpiresAt: task.downloadUrl
      ? moment(task.expiresAt).format(`[Expires on] MM/DD/YYYY [at] hh:mm a`)
      : null,
    isNew: task.status === "Enqueued",
    isCompleted: task.status === "Ready" || task.status === "Failed",
    isVisible: task.status !== "Expired",
    isAcknowledged: task.acknowledgedAt !== undefined,
    isActive:
      (task.status === "Ready" || task.status === "Failed") &&
      task.acknowledgedAt === undefined,
    totalTime: getTotalTime(task.enqueuedAt, task.finishedAt)
  };
}

export default {
  getBackgroundTasks({ commit }) {
    return API.makeRequest("/admins/background_tasks.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "backgroundTasks",
        data: (res.data.backgroundTasks || [])
          .sort(
            (a, b) =>
              new Date(b.enqueuedAt).valueOf() -
              new Date(a.enqueuedAt).valueOf()
          )
          .map(taskAdapter)
          .filter((task) => task.isVisible)
      });
    });
  },
  acknowledgeBackgroundTasks() {
    return API.makeRequest("/admins/background_tasks/acknowledge", {
      method: "PATCH"
    });
  },
  expireBackgroundTask({ commit, state }, id) {
    return API.makeRequest(`/admins/background_tasks/${id}`, {
      method: "DELETE"
    }).then(() => {
      commit("replace", {
        state: "backgroundTasks",
        data: state.backgroundTasks.filter((task) => task.id !== id)
      });
    });
  },
  updateBackgroundTasks({ commit, state }, task) {
    if (!task) {
      return;
    }

    const existingTask = state.backgroundTasks.find((t) => t.id === task.id);
    const updatedTask = { ...existingTask, ...taskAdapter(task) };

    // Ignore any messages received too late out of order
    if (existingTask && existingTask.updatedAt > updatedTask.updatedAt) {
      return;
    }

    const updatedList = existingTask
      ? state.backgroundTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        )
      : [updatedTask, ...state.backgroundTasks];

    return commit("replace", {
      state: "backgroundTasks",
      data: updatedList.filter((task) => task.isVisible)
    });
  }
};
